import { useEffect, useState, useCallback } from 'react';
import { useLocalStorage } from 'react-use';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';

export interface PersistentAutocompleteProps<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'onChange' | 'value' | 'defaultValue'> {
  /**
   * The localStorage key to use for persisting the selected value
   */
  storageKey: string;

  /**
   * Custom onChange handler that receives the selected value
   */
  onChange?: (value: T | null) => void;

  /**
   * Default value to use (takes precedence over localStorage)
   */
  defaultValue?: T | null;

  /**
   * Controlled value (takes precedence over localStorage and defaultValue)
   */
  value?: T | null;
}

/**
 * A drop-in replacement for Material-UI Autocomplete with localStorage persistence
 *
 * @example
 * ```tsx
 * <PersistentAutocomplete
 *   storageKey="my-autocomplete-selection"
 *   options={options}
 *   getOptionLabel={(option) => option.name}
 *   onChange={(value) => console.log('Selected:', value)}
 *   renderInput={(params) => <TextField {...params} label="Select Option" />}
 * />
 * ```
 */
export const PersistentAutocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  storageKey,
  onChange,
  defaultValue = null,
  value = undefined,
  options = [],
  ...autocompleteProps
}: PersistentAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>): React.JSX.Element => {
  // Use localStorage hook for persistent storage with parameterized type T
  const [storedValue, setStoredValue] = useLocalStorage<T | null>(storageKey, null);

  // State to manage the selected value
  const [selectedValue, setSelectedValue] = useState<T | null>(null);

  // State to track if we've attempted to load initial value
  const [hasLoadedInitialValue, setHasLoadedInitialValue] = useState(false);

  // Enhanced onChange handler that also saves to localStorage
  const handleChange = useCallback(
    (newValue: T | null) => {
      // Save to localStorage
      setStoredValue(newValue);

      // Update internal state
      setSelectedValue(newValue);

      // Call the original onChange handler if provided
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange, setStoredValue],
  );

  // Set initial value after options are loaded
  useEffect(() => {
    if (options.length > 0 && !hasLoadedInitialValue && value === undefined) {
      let targetInitialValue: T | null = null;

      // Determine what initial value to use (explicit prop or localStorage)
      if (defaultValue !== null) {
        targetInitialValue = defaultValue;
      } else if (storedValue !== null && storedValue !== undefined) {
        targetInitialValue = storedValue;
      }

      // Find matching option and set as selected value
      if (targetInitialValue !== null) {
        // Use default equality check
        const matchingOption =
          (options as T[]).find(option => {
            // For objects, do a deep comparison of JSON representations
            return JSON.stringify(option) === JSON.stringify(targetInitialValue);
          }) || null;

        if (matchingOption) {
          setSelectedValue(matchingOption);

          // Call onChange to notify parent components when value is restored from localStorage
          if (onChange) {
            onChange(matchingOption);
          }
        }
      }

      setHasLoadedInitialValue(true);
    }
  }, [options, defaultValue, hasLoadedInitialValue, value, storedValue, onChange]);

  // Use controlled value if provided, otherwise use internal state
  const currentValue = value !== undefined ? value : selectedValue;

  return (
    <Autocomplete
      {...autocompleteProps}
      options={options}
      value={currentValue as any}
      onChange={(_, newValue) => {
        handleChange(newValue as T | null);
      }}
    />
  );
};

/**
 * Utility function to clear a stored value from localStorage
 * @param storageKey The localStorage key to clear
 */
export const clearStoredValue = (storageKey: string): void => {
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    // Silently fail if localStorage is not available
    // Error is intentionally ignored for graceful degradation
  }
};
